import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import logo from '../images/logowithname2.png';

const primaryColor = '#5757b0';
const primaryColorTransparent = 'rgba(87, 87, 176, 0.8)';
const hoverColor = '#7777d0';

const LoadingScreen = ({ status, updateMessage }) => {
  const { t } = useTranslation();
  const [tick, setTick] = React.useState(0);
  const startAtRef = React.useRef(Date.now());
  const lastUpdateRef = React.useRef(Date.now());

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    if (updateMessage) {
      lastUpdateRef.current = Date.now();
    }
  }, [updateMessage]);

  const widgetProgress = React.useMemo(() => {
    if (!updateMessage || typeof updateMessage !== 'string') return null;

    const stepMatch = updateMessage.match(/Creating widget\s+(\d+)\s*\/\s*(\d+)(?::\s*(.+))?/i);
    if (stepMatch) {
      return {
        current: Number(stepMatch[1]),
        total: Number(stepMatch[2]),
        title: stepMatch[3] ? stepMatch[3].replace(/\.+$/, '').trim() : ''
      };
    }

    const totalMatch = updateMessage.match(/Creating\s+(\d+)\s+dashboard widgets?/i);
    if (totalMatch) {
      return {
        current: 0,
        total: Number(totalMatch[1]),
        title: ''
      };
    }

    const doneMatch = updateMessage.match(/Successfully created\s+(\d+)\s+dashboard widget/i);
    if (doneMatch) {
      const count = Number(doneMatch[1]);
      return {
        current: count,
        total: count,
        title: '',
        done: true
      };
    }

    return null;
  }, [updateMessage]);

  const widgetProgressValue = React.useMemo(() => {
    if (!widgetProgress || !widgetProgress.total) return null;
    const raw = (widgetProgress.current / widgetProgress.total) * 100;
    return Math.max(0, Math.min(100, Math.round(raw)));
  }, [widgetProgress]);

  const elapsedSeconds = Math.floor((Date.now() - startAtRef.current) / 1000);
  const secondsSinceUpdate = Math.floor((Date.now() - lastUpdateRef.current) / 1000);
  const dots = '.'.repeat((tick % 3) + 1);
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <Box 
      sx={{ 
        backgroundColor: '#f7f7ff', 
        background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 100%)',
        backgroundImage: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        position: 'relative',
        textAlign: 'center'
      }}
    >
      <Box 
        sx={{ 
          maxWidth: '900px',
          width: '100%',
          mx: 'auto'
        }}
      >
        {/* Logo Section - Centered above the title */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4,
            mt: { xs: 2, sm: 4 },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              mb: 2,
              animation: 'fadeInUp 1s ease-out',
              '@keyframes fadeInUp': {
                '0%': { opacity: 0, transform: 'translateY(20px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: '-20px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${primaryColorTransparent} 0%, transparent 70%)`,
                animation: 'pulse 3s infinite ease-in-out',
                opacity: 0.4,
                '@keyframes pulse': {
                  '0%': { transform: 'scale(0.9)', opacity: 0.3 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.5 },
                  '100%': { transform: 'scale(0.9)', opacity: 0.3 }
                }
              }}
            />
            <img
              src={logo}
              alt="DataUnfold Logo"
              style={{
                width: '100px',
                height: '100px',
                position: 'relative',
                zIndex: 2,
                filter: `drop-shadow(0 0 20px ${primaryColorTransparent})`,
              }}
            />
          </Box>
        </Box>

        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            color: primaryColor,
            fontWeight: 600,
            mb: 4,
            fontSize: '2rem'
          }}
        >
          {status === 'updating' ? t('addcase.processing') : t('addcase.add_in_progress')}
        </Typography>

        {/* Loading Animation Card */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 4
        }}>
          {/* Pulsing circle behind spinner */}
          <Box sx={{
            position: 'relative',
            mb: 3
          }}>
            <Box sx={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${primaryColorTransparent} 0%, transparent 70%)`,
              animation: 'pulse-ring 1.5s infinite',
              '@keyframes pulse-ring': {
                '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                '50%': { transform: 'scale(1.2)', opacity: 0.3 },
                '100%': { transform: 'scale(0.8)', opacity: 0.5 }
              },
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }} />

            <Box sx={{
              width: '60px',
              height: '60px',
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              {/* Dual spinner effect */}
              <Box sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: `3px solid transparent`,
                borderTopColor: primaryColor,
                borderLeftColor: primaryColor,
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
              <Box sx={{
                position: 'absolute',
                width: '70%',
                height: '70%',
                borderRadius: '50%',
                border: `3px solid transparent`,
                borderBottomColor: hoverColor,
                borderRightColor: hoverColor,
                animation: 'spin-reverse 1.5s linear infinite',
                '@keyframes spin-reverse': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(-360deg)' }
                }
              }} />
            </Box>
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: primaryColor,
              fontWeight: 500,
              mb: 2,
              fontSize: '1.1rem'
            }}
          >
            {t('addcase.preparing_analysis')}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: updateMessage ? primaryColor : 'text.secondary',
              maxWidth: '600px',
              textAlign: 'center',
              lineHeight: 1.6,
              fontStyle: 'italic',
              opacity: updateMessage ? 1 : 0.8,
              position: 'relative',
              animation: updateMessage ? 'fadeIn 0.5s ease' : 'none',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              },
              '&::after': updateMessage ? {
                content: '""',
                position: 'absolute',
                right: '-8px',
                bottom: '2px',
                width: '6px',
                height: '16px',
                backgroundColor: primaryColor,
                opacity: 0.8,
                animation: 'blink 1s infinite step-end',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 0 },
                  '50%': { opacity: 1 }
                }
              } : {}
            }}
          >
            {updateMessage || t('addcase.processing_message')}
          </Typography>

          {!updateMessage && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                color: primaryColor,
                fontWeight: 600,
                letterSpacing: 0.2
              }}
            >
              {t('addcase.still_working', 'Still working')}
              {dots} ({formatDuration(elapsedSeconds)})
            </Typography>
          )}

          {updateMessage && secondsSinceUpdate >= 6 && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                color: 'text.secondary'
              }}
            >
              {t('addcase.last_update', 'Last update')}: {formatDuration(secondsSinceUpdate)} ago
            </Typography>
          )}

          {widgetProgress && widgetProgress.total ? (
            <Box
              sx={{
                mt: 2,
                width: '100%',
                maxWidth: '420px',
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                border: '1px solid rgba(87, 87, 176, 0.18)',
                backgroundColor: 'rgba(87, 87, 176, 0.06)',
                boxShadow: '0 6px 16px rgba(87, 87, 176, 0.08)',
                textAlign: 'left'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                <Typography variant="body2" sx={{ color: primaryColor, fontWeight: 600 }}>
                  {t('addcase.widget_progress', 'Widgets generated')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {widgetProgress.current} / {widgetProgress.total}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '999px',
                  overflow: 'hidden',
                  backgroundColor: 'rgba(87, 87, 176, 0.15)',
                  mb: widgetProgress.title ? 1 : 0
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${widgetProgressValue ?? 0}%`,
                    background: `linear-gradient(90deg, ${primaryColor}, ${hoverColor})`,
                    transition: 'width 0.35s ease'
                  }}
                />
              </Box>
              {widgetProgress.title && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {t('addcase.widget_progress_current', 'Currently creating')}: {widgetProgress.title}
                </Typography>
              )}
            </Box>
          ) : null}
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          mt: 2
        }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: '600px',
              lineHeight: 1.5,
              textAlign: 'center'
            }}
          >
            {status === 'completed' && ` ${t('addcase.redirect_message')}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingScreen;
